/**
 * @fileoverview Animated hamburger menu button component with smooth transition effects.
 * Transforms from hamburger to X icon with custom easing and accessibility support.
 */

import { useState } from 'react';
import { motion, Transition, Variants } from 'motion/react';
import type { MenuButtonProps } from '../types/components';

/**
 * Animated hamburger menu button with smooth open/close transitions.
 *
 * @description Creates a hamburger menu button that smoothly transforms into an X
 * when clicked. Features custom easing, proper accessibility labels, and clean animations.
 * Commonly used for mobile navigation toggles.
 *
 * @param {MenuButtonProps} props - Component properties
 * @param {() => void} [props.onClick] - Click event handler for menu toggle
 * @param {string} [props.className=''] - Additional CSS classes for styling
 *
 * @returns {JSX.Element} Animated menu button component
 *
 * @example
 * ```tsx
 * // Basic menu button
 * <MenuButton onClick={() => setMenuOpen(!menuOpen)} />
 *
 * // With custom styling
 * <MenuButton
 *   onClick={handleMenuToggle}
 *   className="hover:bg-gray-100 rounded-md p-2"
 * />
 * ```
 */
const MenuButton = ({ onClick, className = '' }: MenuButtonProps) => {
  /** Controls the visual state of the menu button (hamburger vs X) */
  const [isOpen, setIsOpen] = useState(false);

  /** Animation variants for top line - rotates and moves to form X */
  const line1Variants: Variants = {
    closed: { y: -6, rotate: 0 },
    open: { y: 0, rotate: 45 },
  };

  /** Animation variants for bottom line - rotates and moves to form X */
  const line2Variants: Variants = {
    closed: { y: 6, rotate: 0 },
    open: { y: 0, rotate: -45 },
  };

  /** Smooth transition with power2.out easing curve */
  const transition: Transition = {
    duration: 0.3,
    ease: [0.76, 0, 0.24, 1], // Custom cubic-bezier equivalent to power2.out
  };

  /**
   * Handles button click - toggles visual state and calls parent onClick handler.
   */
  const handleClick = (): void => {
    setIsOpen(!isOpen);
    if (onClick) onClick();
  };

  return (
    <button
      onClick={handleClick}
      className={`w-8 h-8 relative focus:outline-none ${className}`}
      aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        {/* Top line - transforms to top part of X */}
        <motion.div
          className="absolute w-6 h-0.5 bg-white"
          variants={line1Variants}
          initial="closed"
          animate={isOpen ? 'open' : 'closed'}
          transition={transition}
          style={{ transformOrigin: 'center' }}
        />

        {/* Bottom line - transforms to bottom part of X */}
        <motion.div
          className="absolute w-6 h-0.5 bg-white"
          variants={line2Variants}
          initial="closed"
          animate={isOpen ? 'open' : 'closed'}
          transition={transition}
          style={{ transformOrigin: 'center' }}
        />
      </div>
    </button>
  );
};

export default MenuButton;
